import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import ProgramAdminScreen from './ProgramAdminScreen';
import PublicPreviewModal from './PublicPreviewModal';
import * as Api from './ProgramAdminApi';
import { notifyOf, personById } from './programAdminModel';

// The route component behind /admin/program: it owns the data (event options, the program and the
// people of the selected event) and every call that changes it. The screen stays presentational.
//
// Loading: GET /api/cms/events/options once, then GET .../program and GET .../people for the selected
// event, every time that event changes. After each write the program and the people are read again -
// nothing is kept locally, so the screen always shows what the server holds.
//
// Emails: the frontend never writes one. Before it offers an action the screen asks for the
// backend's own preview (invitation.RenderedMail, program.CancellationNotice.Preview) and shows it,
// editable. An invitation and a reminder edit the preview's `template` (the {{placeholder}} text,
// never the finished email), a notice its plain subject/body; what the admin changed goes back with
// the write where the API takes it - the `notify` messages of a cancellation or a withdrawal, and
// the `template` of an invitation or reminder send (only what was changed, unchanged fields omitted).
// `refreshPreview` runs the preview route again with an edited template, so the dialog shows the
// email the edit would send. An edit that drops the personal link is refused with 400 and shown.
//
// The deep link ?event={eventId}&spot={assignmentId} is honoured: the event is selected and the spot
// is opened in the side panel (program.ProgramScreenLinks is the one definition of these links).

const blankToNull = (value) => {
  const text = value == null ? '' : String(value).trim();
  return text === '' ? null : text;
};
const numberOrNull = (value) => {
  const text = value == null ? '' : String(value).trim();
  return text === '' ? null : Number(text);
};

// The admin's edited email of an invitation or reminder (InvitationMail.TemplateOverride). The
// previewed template is the baseline (RenderedMail.template): only what the admin changed is sent
// back, so an untouched dialog omits `template` and the backend keeps its own copy. An edit that
// drops the personal link is refused with 400 and shown in the dialog. A notice has no template -
// its plain subject/body are sent as the notify message instead (notifyOf).
const templateOf = (email) => {
  const template = email?.template;
  if (!template) return null;
  const subjectChanged = template.subject !== template.originalSubject;
  const htmlChanged = template.html !== template.originalHtml;
  if (!subjectChanged && !htmlChanged) return null;
  return {
    ...(subjectChanged ? { subject: template.subject } : {}),
    ...(htmlChanged ? { html: template.html } : {}),
    highlight: true,
  };
};

const ProgramAdminContainer = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const linkedEventId = Number(searchParams.get('event')) || null;
  const linkedSpotId = Number(searchParams.get('spot')) || null;

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(linkedEventId);
  const [program, setProgram] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const load = useCallback(async (id) => {
    if (id == null) { setProgram(null); setPeople([]); return; }
    setLoading(true);
    setError(null);
    try {
      const [view, rows] = await Promise.all([Api.fetchProgram(id, true), Api.fetchPeople(id)]);
      setProgram(view);
      setPeople(rows || []);
    } catch (err) {
      // A refused event (403) must not leave the previous event's program on screen under the new
      // selection; the screen shows its empty state for the answered status.
      setProgram(null);
      setPeople([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // The event dropdown is read once; a list without an event leaves the screen with nothing to load.
  const loadEvents = useCallback(async () => {
    try {
      const options = (await Api.fetchEventOptions()) || [];
      setEvents(options);
      setEventId((current) => current ?? (options.find((o) => o.main)?.id ?? options.find((o) => !o.past)?.id ?? options[0]?.id ?? null));
      if (options.length === 0) setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { load(eventId); }, [eventId, load]);

  const reload = useCallback(() => load(eventId), [eventId, load]);
  // The plan of "put the suggested person on this spot", which its dialog shows before the admin chooses.
  const fetchSuggestion = useCallback((assignmentId) => Api.fetchSuggestedPerson(eventId, assignmentId), [eventId]);
  const retry = useCallback(() => {
    setError(null);
    if (events.length === 0) loadEvents();
    load(eventId);
  }, [eventId, events.length, load, loadEvents]);

  const contactOf = useCallback((personId) => personById(people, personId)?.contactEmail || '', [people]);

  // ---------------------------------------------------------------- the email previews

  const previewEmails = useCallback(async (target) => {
    if (!target || eventId == null) return [];
    if (target.kind === 'invite') {
      const out = [];
      for (const person of target.people || []) {
        const mail = await Api.previewInvitation(eventId, {
          personId: person.personId, assignmentIds: person.assignmentIds, changeSetIds: null,
        });
        // `person.to`: an address saved just before this dialog (the people list may still be the old one).
        const to = person.to || contactOf(person.personId);
        out.push({
          personId: person.personId, name: person.name, to,
          hasEmail: !!to, subject: mail.subject, html: mail.html,
          template: mail.template, placeholders: mail.placeholders, templateProblems: mail.templateProblems,
        });
      }
      return out;
    }
    if (target.kind === 'remind') {
      const out = [];
      for (const person of target.people || []) {
        const mail = await Api.previewReminder(eventId, { personId: person.personId });
        out.push({
          personId: person.personId, name: person.name, to: contactOf(person.personId),
          hasEmail: !!contactOf(person.personId), subject: mail.subject, html: mail.html,
          template: mail.template, placeholders: mail.placeholders, templateProblems: mail.templateProblems,
        });
      }
      return out;
    }
    if (target.kind === 'withdraw') {
      return (await Api.previewAssignmentNotices(eventId, target.assignmentId, 'withdraw')).map((notice) => ({
        personId: notice.personId, name: notice.personName, to: contactOf(notice.personId),
        hasEmail: notice.hasEmail, assignmentIds: notice.assignmentIds,
        subject: notice.subject, html: notice.body, previewHtml: notice.bodyWithImages,
      }));
    }
    if (target.kind === 'uninvite' || target.kind === 'replacePerson' || target.kind === 'removeSpot') {
      return (await Api.previewAssignmentNotices(eventId, target.assignmentId)).map((notice) => ({
        personId: notice.personId, name: notice.personName, to: contactOf(notice.personId),
        hasEmail: notice.hasEmail, assignmentIds: notice.assignmentIds,
        subject: notice.subject, html: notice.body, previewHtml: notice.bodyWithImages,
      }));
    }
    if (target.kind === 'removeEntry') {
      return (await Api.previewEntryNotices(eventId, target.entryId)).map((notice) => ({
        personId: notice.personId, name: notice.personName, to: contactOf(notice.personId),
        hasEmail: notice.hasEmail, assignmentIds: notice.assignmentIds,
        subject: notice.subject, html: notice.body, previewHtml: notice.bodyWithImages,
      }));
    }
    return [];
  }, [contactOf, eventId]);

  // "Refresh preview" in a dialog: the same preview route again, with the admin's edited template, so
  // the email on screen is the one the edit would send. The answer is the rendered mail (RenderedMail);
  // a refusal (400, the missing link) comes back to the dialog as its message.
  const refreshPreview = useCallback(async (target, email) => {
    const template = { subject: email.template.subject, html: email.template.html, highlight: true };
    if (target?.kind === 'invite') {
      return Api.previewInvitation(eventId, {
        personId: email.personId, assignmentIds: email.assignmentIds, changeSetIds: null, template,
      });
    }
    return Api.previewReminder(eventId, { personId: email.personId, template });
  }, [eventId]);

  // ---------------------------------------------------------------- the writes

  // The change routes' one preview: who is affected, by what, and the email each chosen person
  // would get (plus the editable template and its placeholders). Nothing is saved or sent. The
  // change kind decides the route: a create (Add block, Add child) posts its `create` body to
  // .../entries/changes/preview, a removal to .../entries/{id}/removal/preview, everything else
  // (Edit and every move) to .../entries/{id}/changes/preview.
  const previewChange = useCallback((target, change, notifyAssignmentIds = [], template = null) => {
    const body = { ...change, notifyAssignmentIds, ...(template ? { template } : {}) };
    if (target?.kind === 'addChild' || target?.kind === 'addBlock') {
      return Api.previewEntryCreate(eventId, body);
    }
    if (target?.kind === 'removeEntry') {
      return Api.previewEntryRemoval(eventId, target.entryId, body);
    }
    return Api.previewEntryChange(eventId, target.entryId, body);
  }, [eventId]);

  // The admin unticked "send this email" (only an email with an address has a box to untick).
  const unticked = (email) => !!email && email.hasEmail !== false && email.send === false;

  const runAction = useCallback(async (target, { emails, fields, change, notifyAssignmentIds, template }) => {
    if (!target) return null;
    let result = null;
    const editedEmailOf = (personId) => (emails || []).find((e) => e.personId === personId) || null;
    switch (target.kind) {
      case 'invite':
        for (const person of target.people || []) {
          // An unticked "send this email" means this person is not invited now.
          if (unticked(editedEmailOf(person.personId))) continue;
          const template = templateOf(editedEmailOf(person.personId));
          await Api.sendInvitation(eventId, {
            personId: person.personId, assignmentIds: person.assignmentIds, changeSetIds: null,
            ...(template ? { template } : {}),
          });
        }
        break;
      case 'remind': {
        // The reminder is one global template rendered per person: the admin's edit is sent once for
        // the whole send, so the first edited email is the template (the dialog shows one person).
        const edited = (emails || []).map(templateOf).find(Boolean) || null;
        const reminded = (target.people || []).filter((person) => !unticked(editedEmailOf(person.personId)));
        if (reminded.length > 0) await Api.sendReminders(eventId, reminded.map((person) => person.personId), edited);
        break;
      }
      case 'uninvite':
        await Api.uninviteAssignment(eventId, target.assignmentId, notifyOf(emails));
        break;
      case 'withdraw':
        await Api.withdrawAssignment(eventId, target.assignmentId, notifyOf(emails));
        break;
      case 'removeSpot':
        await Api.removeAssignment(eventId, target.assignmentId, notifyOf(emails));
        break;
      case 'removeEntry':
        // Without `change` this is today's removal: only the cancellation choice. With a change
        // (the gap box was ticked) the fixed change - its `notify` included - goes with the chosen
        // people and the one-time template.
        result = await Api.removeEntry(eventId, target.entryId, change
          ? { ...change, notifyAssignmentIds: notifyAssignmentIds || [], ...(template ? { template } : {}) }
          : { notify: notifyOf(emails) });
        break;
      case 'replacePerson':
        await Api.replaceAssignmentPerson(eventId, target.assignmentId, {
          personId: numberOrNull(fields.personId), notify: notifyOf(emails),
        });
        break;
      case 'decline':
        await Api.declineAssignment(eventId, target.assignmentId, {
          replacementName: blankToNull(fields.replacementName), replacementEmail: blankToNull(fields.replacementEmail),
        });
        break;
      case 'moveSpot':
        await Api.moveAssignment(eventId, target.assignmentId, { entryId: Number(fields.entryId) });
        break;
      case 'addSpot':
        await Api.addAssignment(eventId, {
          entryId: target.entryId, role: fields.role, placeholderLabel: blankToNull(fields.placeholderLabel),
        });
        break;
      case 'addChild':
      case 'addBlock':
        // ProgramRequests.CreateChange: the fixed `create` body with its `times`, the chosen
        // people and the one-time template. Nothing is rebuilt from the form here.
        result = await Api.createEntryChange(eventId, {
          ...change,
          notifyAssignmentIds: notifyAssignmentIds || [],
          ...(template ? { template } : {}),
        });
        break;
      case 'editEntry':
      case 'moveEntry':
        // ProgramRequests.EntryChange: the fixed change the dialog built (content and room, or the
        // move), `times` included, through the change route so the admin chooses who is told.
        result = await Api.changeEntry(eventId, target.entryId, {
          ...change,
          notifyAssignmentIds: notifyAssignmentIds || [],
          ...(template ? { template } : {}),
        });
        break;
      case 'useSuggestion': {
        // What the admin saw (the invitation of the plan the dialog showed) and chose (a person id,
        // or 'create') goes back with the write: the backend refuses it when the spot, its
        // suggestion or the people it offers changed meanwhile.
        const choice = fields.choice || target.choice;
        await Api.applySuggestedPerson(eventId, target.assignmentId, choice === 'create'
          ? { invitationId: target.invitationId, action: 'CREATE', personId: null }
          : { invitationId: target.invitationId, action: 'USE_EXISTING', personId: Number(choice) });
        break;
      }
      case 'setContactEmail':
        // The address an invitation needs, saved on the person first (the dialog then opens the invitation).
        await Api.savePersonContactEmail(target.personId, fields.contactEmail.trim());
        break;
      case 'setShowPublicly':
        // AssignmentRequests.Fields replaces the labels and the note, so the current ones go along.
        await Api.updateAssignment(eventId, target.assignmentId, {
          showPublicly: target.value, groupLabel: target.groupLabel, displayNote: target.displayNote,
          placeholderLabel: target.placeholderLabel, sortOrder: null,
        });
        break;
      case 'stopReminders':
        await Api.stopReminders(eventId, target.personId);
        break;
      case 'resumeReminders':
        await Api.resumeReminders(eventId, target.personId);
        break;
      default:
        break;
    }
    await load(eventId);
    return result;
  }, [eventId, load]);

  // ---------------------------------------------------------------- the public preview

  const openPreview = useCallback(async () => {
    if (eventId == null) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    try {
      const [view, peopleView] = await Promise.all([Api.fetchPublicPreview(eventId), Api.fetchPublicPreviewPeople(eventId)]);
      setPreviewData({ program: view, people: peopleView });
    } catch (err) {
      setPreviewError(typeof err?.response?.data === 'string' && err.response.data
        ? err.response.data : 'Could not load the public preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [eventId]);

  // The heading of the PDF export: the event's name in the admin's language (options carry both).
  const eventTitle = useMemo(() => {
    const option = events.find((e) => e.id === eventId);
    if (!option) return undefined;
    return (language === 'de' ? option.nameDe || option.nameEn : option.nameEn || option.nameDe) || undefined;
  }, [events, eventId, language]);

  const linkedSpot = useMemo(
    () => (linkedEventId != null && linkedEventId === eventId ? linkedSpotId : null),
    [linkedEventId, linkedSpotId, eventId],
  );

  return (
    <>
      <ProgramAdminScreen
        events={events}
        eventId={eventId}
        program={program}
        people={people}
        initialSpotId={linkedSpot ?? undefined}
        language={language}
        loading={loading}
        error={error}
        onRetry={retry}
        onSelectEvent={setEventId}
        previewEmails={previewEmails}
        refreshPreview={refreshPreview}
        previewChange={previewChange}
        runAction={runAction}
        searchPeople={Api.searchPeople}
        createPerson={Api.createPerson}
        findPersonMatches={Api.findPersonMatches}
        fetchSuggestion={fetchSuggestion}
        onPreviewPublic={openPreview}
        onReload={reload}
        onDownloadMaterials={Api.downloadProgramMaterialsZip}
      />
      <PublicPreviewModal
        open={previewOpen}
        loading={previewLoading}
        error={previewError}
        program={previewData?.program}
        people={previewData?.people}
        language={language}
        eventId={eventId}
        eventTitle={eventTitle}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
};

export default ProgramAdminContainer;
